import { useState, useCallback } from 'react';
import { Badge, Spinner } from '@klaro7/ui';
import { buildAllStepSchemas } from '@klaro7/validators';
import type { StepDefinition, FieldValidationRules } from '@klaro7/shared-types';

import { useQuestionnaireDefinition, useUpdateStepDefinition } from '../hooks/useQuestionnaireDefinition';
import { DynamicStepRenderer } from './DynamicStepRenderer';
import { StepDefinitionEditor } from './StepDefinitionEditor';
import { ProgressBar } from '@/app/routes/questionnaire';

// ---------------------------------------------------------------------------
// Badge variant helpers
// ---------------------------------------------------------------------------

const TYPE_BADGE_VARIANT: Record<string, 'default' | 'primary' | 'secondary' | 'info'> = {
  text: 'default',
  email: 'info',
  tel: 'info',
  select: 'primary',
  radio: 'primary',
  'checkbox-group': 'primary',
  textarea: 'secondary',
  boolean: 'default',
  range: 'secondary',
  lingue: 'info',
};

function getOptionsText(field: { type: string; options?: { label: string }[]; validation?: FieldValidationRules }): string {
  if (field.options && field.options.length > 0) {
    return field.options.map((o) => o.label).join(', ');
  }
  if (field.validation) {
    const parts: string[] = [];
    if (field.validation.minLength != null) parts.push(`min ${field.validation.minLength}`);
    if (field.validation.maxLength != null) parts.push(`max ${field.validation.maxLength}`);
    if (field.validation.min != null) parts.push(`min ${field.validation.min}`);
    if (field.validation.max != null) parts.push(`max ${field.validation.max}`);
    if (field.validation.pattern) parts.push(`pattern: ${field.validation.pattern}`);
    if (field.validation.isUrl) parts.push('URL');
    if (field.validation.isEmail) parts.push('Email');
    if (field.validation.isLiteral) parts.push('Letterale');
    if (parts.length > 0) return parts.join(', ');
  }
  return '-';
}

// ---------------------------------------------------------------------------
// QuestionnairePreview
// ---------------------------------------------------------------------------

function QuestionnairePreview({
  steps,
  onClose,
}: {
  steps: StepDefinition[];
  onClose: () => void;
}) {
  const totalSteps = steps.length;
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState<Record<number, Record<string, unknown>>>(() => {
    const initial: Record<number, Record<string, unknown>> = {};
    for (const step of steps) {
      initial[step.step] = {};
    }
    return initial;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewMessage, setPreviewMessage] = useState('');

  const schemas = buildAllStepSchemas(steps);

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      setStepData((prev) => ({
        ...prev,
        [currentStep]: { ...prev[currentStep], [field]: value },
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [currentStep],
  );

  const validateStep = (step: number): boolean => {
    const schema = schemas[step];
    if (!schema) return true;
    const result = schema.safeParse(stepData[step]);
    if (result.success) {
      setErrors({});
      return true;
    }
    const newErrors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join('.');
      if (!newErrors[key]) {
        newErrors[key] = issue.message;
      }
    }
    setErrors(newErrors);
    return false;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep === totalSteps) {
      setPreviewMessage('Questa e solo un\'anteprima - nessun dato viene inviato');
      return;
    }
    setCurrentStep((s) => s + 1);
    setErrors({});
    setPreviewMessage('');
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
      setErrors({});
      setPreviewMessage('');
    }
  };

  const handleStepClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      setErrors({});
      setPreviewMessage('');
    }
  };

  const currentStepDef = steps.find((s) => s.step === currentStep);

  return (
    <div className="fixed inset-0 z-50 bg-neutral-50 overflow-auto">
      {/* Preview banner */}
      <div className="bg-primary-600 text-white text-center py-2 px-4 text-sm font-medium">
        Modalita Anteprima — Questa e una simulazione del questionario come lo vedrebbe un professionista
      </div>

      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <span className="text-xl font-bold text-primary-600">Anteprima Questionario</span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <ProgressBar currentStep={currentStep} totalSteps={totalSteps} onStepClick={handleStepClick} />

        <div className="bg-white rounded-xl border border-neutral-200 p-6 sm:p-8">
          {currentStepDef && (
            <DynamicStepRenderer
              stepDef={currentStepDef}
              data={stepData[currentStep] || {}}
              errors={errors}
              onChange={handleFieldChange}
            />
          )}

          {previewMessage && (
            <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-800 font-medium text-center">
              {previewMessage}
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-neutral-100">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors ${
                currentStep === 1
                  ? 'border-neutral-200 text-neutral-300 cursor-not-allowed'
                  : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              Indietro
            </button>
            <button
              onClick={handleNext}
              className="px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              {currentStep === totalSteps ? 'Invia Candidatura' : 'Avanti'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuestionnaireStructure
// ---------------------------------------------------------------------------

export function QuestionnaireStructure() {
  const [showPreview, setShowPreview] = useState(false);
  const [editingStep, setEditingStep] = useState<StepDefinition | null>(null);

  const { data: definition, isLoading } = useQuestionnaireDefinition();
  const updateMutation = useUpdateStepDefinition();

  const handleSaveStep = async (updated: StepDefinition) => {
    await updateMutation.mutateAsync({
      step: updated.step,
      payload: {
        step: updated.step,
        name: updated.name,
        description: updated.description,
        fields: updated.fields,
      },
    });
    setEditingStep(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const steps = definition?.steps ?? [];

  return (
    <>
      {showPreview && steps.length > 0 && (
        <QuestionnairePreview steps={steps} onClose={() => setShowPreview(false)} />
      )}

      {editingStep && (
        <StepDefinitionEditor
          stepDef={editingStep}
          onSave={handleSaveStep}
          onCancel={() => setEditingStep(null)}
          isSaving={updateMutation.isPending}
        />
      )}

      <div className="space-y-4">
        {/* Preview button */}
        <div>
          <button
            onClick={() => setShowPreview(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Anteprima Questionario
          </button>
        </div>

        {/* Step accordion cards */}
        {steps.map((step) => (
          <details key={step.step} className="group overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <summary className="flex cursor-pointer items-center gap-3 px-5 py-4 hover:bg-neutral-50/50 transition-colors">
              <svg className="h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-bold text-neutral-800">Step {step.step}</span>
              <span className="text-sm font-semibold text-neutral-700">{step.name}</span>
              <span className="text-sm text-neutral-500 hidden sm:inline">— {step.description}</span>
              <Badge variant="default" className="ml-auto flex-shrink-0">
                {step.fields.length} campi
              </Badge>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setEditingStep(step);
                }}
                className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                title="Modifica step"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
              </button>
            </summary>

            <div className="border-t border-neutral-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-neutral-50">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Campo</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Tipo</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Obbligatorio</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Opzioni / Validazione</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {step.fields.map((field) => (
                      <tr key={field.key} className="hover:bg-neutral-50/50 transition-colors">
                        <td className="px-5 py-3 text-sm font-medium text-neutral-800">{field.label}</td>
                        <td className="px-5 py-3">
                          <Badge variant={TYPE_BADGE_VARIANT[field.type] || 'default'}>
                            {field.type}
                          </Badge>
                        </td>
                        <td className="px-5 py-3">
                          {field.required ? (
                            <Badge variant="success">Si</Badge>
                          ) : (
                            <Badge variant="default">No</Badge>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm text-neutral-600 max-w-xs truncate">
                          {getOptionsText(field)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>
        ))}
      </div>
    </>
  );
}
